import React from "react";
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Theme,
  useTheme,
} from "@mui/material";
import { WaitlistEntry } from "../../../models/Swayy/Waitlist";
import { SortType } from "../../../models/SortType";
import AppTableHeaderCell from "../common/pages/AppTableHeaderCell";
import AppTablePagenation from "../common/pages/AppTablePagenation";
import { formatDate } from "../common/functions/formatDate";
import {
  modernEmptyStateSx,
  modernTableBodyCellSx,
  modernTableHeaderCellSx,
  modernTableRowSx,
} from "../../../styles/modernListingPageSx";

interface WaitlistTableProps {
  loading: boolean;
  waitlist: WaitlistEntry[];
  limit: number;
  page_no: number;
  total_count: number;
  sortBy: string;
  sortType: SortType;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => void;
  onRowsPerPageChange: (limit: number) => void;
  onSortChange: (column: string, sortType: SortType) => void;
}

const WaitlistTable = ({
  loading,
  waitlist,
  page_no,
  limit,
  total_count,
  sortBy,
  sortType,
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
}: WaitlistTableProps) => {
  const theme = useTheme<Theme>();

  return (
    <Box>
      {loading ? (
        <Box
          sx={{
            minHeight: "40vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress />
        </Box>
      ) : waitlist.length === 0 ? (
        <Box sx={{ ...modernEmptyStateSx(theme), minHeight: "20vh" }}>
          <Typography variant="body1">No waitlist entries found.</Typography>
        </Box>
      ) : (
        <Box>
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <AppTableHeaderCell
                    minWidth="80px"
                    fontSize="0.75rem"
                    sortColumn="id"
                    sortBy={sortBy}
                    sortDirection={sortType}
                    onSortChange={onSortChange}
                    sx={modernTableHeaderCellSx(theme)}
                  >
                    ID
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    minWidth="220px"
                    fontSize="0.75rem"
                    sx={modernTableHeaderCellSx(theme)}
                  >
                    Email
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    minWidth="160px"
                    fontSize="0.75rem"
                    sx={modernTableHeaderCellSx(theme)}
                  >
                    Name
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    minWidth="120px"
                    fontSize="0.75rem"
                    sx={modernTableHeaderCellSx(theme)}
                  >
                    Phone
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    minWidth="160px"
                    fontSize="0.75rem"
                    sortColumn="createdAt"
                    sortBy={sortBy}
                    sortDirection={sortType}
                    onSortChange={onSortChange}
                    sx={modernTableHeaderCellSx(theme)}
                  >
                    Created At
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    minWidth="160px"
                    fontSize="0.75rem"
                    sortColumn="modified_date"
                    sortBy={sortBy}
                    sortDirection={sortType}
                    onSortChange={onSortChange}
                    sx={modernTableHeaderCellSx(theme)}
                  >
                    Modified Date
                  </AppTableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {waitlist.map((entry) => (
                  <TableRow
                    key={entry.id}
                    hover
                    sx={modernTableRowSx(theme)}
                  >
                    <TableCell sx={modernTableBodyCellSx(theme)}>
                      {entry.id}
                    </TableCell>
                    <TableCell sx={modernTableBodyCellSx(theme)}>
                      {entry.email || "—"}
                    </TableCell>
                    <TableCell sx={modernTableBodyCellSx(theme)}>
                      {entry.name || "—"}
                    </TableCell>
                    <TableCell sx={modernTableBodyCellSx(theme)}>
                      {entry.phone || "—"}
                    </TableCell>
                    <TableCell sx={modernTableBodyCellSx(theme)}>
                      {entry.createdAt ? formatDate(entry.createdAt) : "—"}
                    </TableCell>
                    <TableCell sx={modernTableBodyCellSx(theme)}>
                      {entry.modified_date
                        ? formatDate(entry.modified_date)
                        : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <AppTablePagenation
            fontSize="12px"
            pageNo={page_no}
            limit={limit}
            totalCount={total_count}
            onPageChange={onPageChange}
            handleRowsChange={onRowsPerPageChange}
          />
        </Box>
      )}
    </Box>
  );
};

export default React.memo(WaitlistTable);
